import {
  FAILED_TO_LOAD_HCAPTCHA_SCRIPT,
  FAILED_TO_LOAD_RECAPTCHA_SCRIPT,
} from './constants';

export const HCAPTCHA_TYPE = 'hcaptcha';
export const RECAPTCHA_TYPE = 'recaptcha';

Pollfish.captcha = (function captcha() {
  let captchaId;
  let isApiReady = false;
  let isRemoved = false;
  let loadedSiteKey;
  let renderContainer;
  return {
    setup({ type = HCAPTCHA_TYPE, ...props }) {
      this.type = type;
      if (type === RECAPTCHA_TYPE) {
        return this.setupRecaptcha(props);
      }
      return this.setupHaptcha(props);
    },
    setupRecaptcha({ siteKey }) {
      if (isApiReady) {
        // it's already loaded so no need to setup again
        return null;
      }
      return new Promise((resolve) => {
        this.loadRecaptchaScript(siteKey, () => {
          isApiReady = true;
          loadedSiteKey = siteKey;
          resolve();
        });
      });
    },
    setupHaptcha({ container, siteKey }) {
      if (!container) {
        throw new Error('Missing container to setup captcha');
      }
      if (!siteKey) {
        throw new Error('Missing siteKey when initialising');
      }
      if (isApiReady && loadedSiteKey === siteKey) {
        // it's already loaded so no need to setup again
        return null;
      }
      if (isApiReady && loadedSiteKey !== siteKey) {
        // we need to re-render since we have a new site key
        loadedSiteKey = siteKey;
        // we also need to reset the container, since you can't hook a second
        // captcha form on the same container
        renderContainer = container;
        this.createWidget(renderContainer);
        this.render(siteKey);
        return null;
      }
      renderContainer = container;
      this.createWidget(renderContainer);
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line consistent-return
        this.loadScript(siteKey, ({ error = null } = {}) => {
          if (error) {
            return reject(error);
          }
          isApiReady = true;
          loadedSiteKey = siteKey;
          resolve(this.render(siteKey));
        });
      });
    },
    loadRecaptchaScript(siteKey, onLoad) {
      window.recaptchaOnLoad = () => {
        onLoad();
      };

      window.recaptchaOnLoadError = () => {
        onLoad({ error: FAILED_TO_LOAD_RECAPTCHA_SCRIPT });
      };

      const script = document.createElement('script');
      const url = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&onload=recaptchaOnLoad`;
      script.src = url;
      script.async = true;
      script.onerror = window.recaptchaOnLoadError;
      document.head.appendChild(script);
    },
    loadScript(siteKey, onLoad) {
      window.hcaptchaOnLoad = () => {
        onLoad();
      };

      window.hcaptchaOnLoadError = () => {
        onLoad({ error: FAILED_TO_LOAD_HCAPTCHA_SCRIPT });
      };

      const script = document.createElement('script');

      const domain = 'https://hcaptcha.pollfish.com';
      const endpoint = encodeURIComponent('https://hcaptcha.pollfish.com');
      const host = encodeURIComponent('hcaptcha.pollfish.com');
      const assetHost = encodeURIComponent(
        'https://assets-hcaptcha.pollfish.com',
      );
      const imghost = encodeURIComponent('https://img-hcaptcha.pollfish.com');
      const url = `${domain}/1/api.js?render=explicit&onload=hcaptchaOnLoad&endpoint=${endpoint}&host=${host}&assethost=${assetHost}&imghost=${imghost}`;
      script.src = url;
      script.async = true;
      script.onerror = window.hcaptchaOnLoadError;

      // const query = generateQuery(params);
      // script.src += query !== '' ? `&${query}` : '';

      document.head.appendChild(script);
    },
    isReady() {
      return typeof window.hcaptcha !== 'undefined';
    },
    render(siteKey) {
      if (!isApiReady) {
        return null;
      }
      const expireCallback = this.handleExpire.bind(this);
      const renderParams = {
        // 'error-callback': this.handleError,
        'expired-callback': expireCallback,
        sitekey: siteKey,
        size: 'invisible',
        // callback: this.handleSubmit,
      };
      captchaId = window.hcaptcha.render(renderContainer, renderParams);
      isRemoved = false;
      return captchaId;
    },
    async execute(siteKey) {
      if (!isApiReady || isRemoved) return null;
      if (this.type === RECAPTCHA_TYPE) {
        return new Promise((resolve, reject) => {
          window.grecaptcha.enterprise.ready(async () => {
            try {
              const token = await window.grecaptcha.enterprise.execute(
                siteKey,
                { fast: true },
              );
              resolve({ response: token });
            } catch (e) {
              reject(e);
            }
          });
        });
      }
      return window.hcaptcha.execute(captchaId, { async: true });
    },
    reset() {
      if (!isApiReady || isRemoved) return;
      if (this.type === RECAPTCHA_TYPE) {
        return;
      }
      // Reset captcha state, removes stored token and unticks checkbox
      window.hcaptcha.reset(captchaId);
    },
    remove() {
      if (!isApiReady || isRemoved) return;
      if (this.type === RECAPTCHA_TYPE) {
        return;
      }
      isRemoved = true;
      window.hcaptcha.remove(captchaId);
    },
    handleExpire() {
      if (!isApiReady || isRemoved) return;
      if (this.type === RECAPTCHA_TYPE) {
        return;
      }
      // If hCaptcha runs into error, reset captcha - hCaptcha
      window.hcaptcha.reset(captchaId);
    },
    handleError() {
      if (!isApiReady || isRemoved) return;
      if (this.type === RECAPTCHA_TYPE) {
        return;
      }
      // If hCaptcha runs into error, reset captcha - hCaptcha
      window.hcaptcha.reset(captchaId);
    },
    createWidget(container) {
      // eslint-disable-next-line no-param-reassign
      container.innerHTML = `
        <div
          id="hcaptcha"
          class="h-captcha"
        ></div>
      `;
    },
  };
})();

export default Pollfish.captcha;
